import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getWhatsNew, WhatsNewMessage, HIDDEN_MESSAGES_STORAGE_KEY } from '../../services/firebase/statusQuo.service';
import { RootState } from '../index';
import logger from '../../utils/logger';

interface StatusQuoState {
  whatsNew: {
    message: WhatsNewMessage;
    isLoading: boolean;
    error: string | null;
  };
  hiddenMessageIds: string[];
}

const initialState: StatusQuoState = {
  whatsNew: {
    message: {
      id: '',
      title: '',
      description: ''
    },
    isLoading: false,
    error: null
  },
  hiddenMessageIds: []
};

// Async thunk to fetch what's new data
export const fetchWhatsNew = createAsyncThunk(
  'statusQuo/fetchWhatsNew',
  async (_, { dispatch }) => {
    return new Promise<any>((resolve) => {
      const unsubscribe = getWhatsNew((data) => {
        resolve(data);
        unsubscribe(); // Stop listening after initial fetch
      });
    });
  }
);

// Async thunk to fetch hidden message IDs from localStorage
export const getHiddenMessageIds = createAsyncThunk(
  'statusQuo/getHiddenMessageIds',
  async () => {
    try {
      const storedData = localStorage.getItem(HIDDEN_MESSAGES_STORAGE_KEY);
      return storedData ? JSON.parse(storedData) : [];
    } catch (error) {
      logger.error('Error getting hidden message IDs from localStorage:', error);
      return [];
    }
  }
);

// Async thunk to hide a message ID
export const hideWhatsNewMessage = createAsyncThunk(
  'statusQuo/hideWhatsNewMessage',
  async (id: string, { getState }) => {
    try {
      const { statusQuo } = getState() as { statusQuo: StatusQuoState };
      // Add current id to existing list, ensure no duplicates, keep only 10 most recent
      const updatedList = [...new Set([...statusQuo.hiddenMessageIds, id])].slice(-10);
      
      // Save to localStorage
      localStorage.setItem(HIDDEN_MESSAGES_STORAGE_KEY, JSON.stringify(updatedList));
      
      return updatedList;
    } catch (error) {
      logger.error('Error hiding message ID in localStorage:', error);
      throw error;
    }
  }
);

const statusQuoSlice = createSlice({
  name: 'statusQuo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetchWhatsNew
      .addCase(fetchWhatsNew.pending, (state) => {
        state.whatsNew.isLoading = true;
        state.whatsNew.error = null;
      })
      .addCase(fetchWhatsNew.fulfilled, (state, action: PayloadAction<any>) => {
        state.whatsNew.isLoading = false;
        state.whatsNew.message = action.payload ? action.payload.message : initialState.whatsNew.message;
      })
      .addCase(fetchWhatsNew.rejected, (state, action) => {
        state.whatsNew.isLoading = false;
        state.whatsNew.error = action.error.message || 'Failed to fetch What\'s New data';
      })
      
      // Handle getHiddenMessageIds
      .addCase(getHiddenMessageIds.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.hiddenMessageIds = action.payload;
      })
      
      // Handle hideWhatsNewMessage
      .addCase(hideWhatsNewMessage.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.hiddenMessageIds = action.payload;
      });
  }
});

// Export selectors
export const selectWhatsNewMessage = (state: RootState) => state.statusQuo.whatsNew.message;
export const selectWhatsNewLoading = (state: RootState) => state.statusQuo.whatsNew.isLoading;
export const selectWhatsNewError = (state: RootState) => state.statusQuo.whatsNew.error;
export const selectHiddenMessageIds = (state: RootState) => state.statusQuo.hiddenMessageIds;

export default statusQuoSlice.reducer;